/**
 * Moss Matrix ingredient library v2: attach merged educational payloads to catalog rows.
 * Safe routine language only. Not medical advice.
 */

import { RICH_LIBRARY_BY_ID, RICH_SEO_BY_ID } from "./ingredientLibraryRichOverrides";

/** @typedef {{ label: string, reason: string, caution?: string }} LibraryPairingV2 */
/** @typedef {{ q: string, a: string }} LibraryFaqV2 */
/**
 * @typedef {Object} IngredientLibraryV2
 * @property {{ title?: string, bullets: string[] }} glance
 * @property {string[]} traditionalParagraphs
 * @property {{ routine: string, taste: string, pairing: string }} whyPeopleChooseIt
 * @property {string[]} nutrientsOrCompounds
 * @property {{ show: boolean, title?: string, paragraphs: string[] }} [detoxFraming]
 * @property {string[]} tasteInGel
 * @property {LibraryPairingV2[]} pairings
 * @property {{ intro: string, placeholders: string[] }} communityNotes
 * @property {string[]} cautionBullets
 * @property {LibraryFaqV2[]} faqs
 * @property {string} [seoSummary]
 * @property {{ label: string, href: string }[]} [ctaLinks]
 */

const DETOX_SECTION_IDS = new Set([
  "dandelion-root",
  "burdock-root",
  "nettle",
  "milk-thistle",
  "spirulina",
  "chlorella",
  "moringa",
  "bladderwrack",
  "wheatgrass",
  "barley-grass",
  "turmeric",
  "ginger",
]);

const INTERNAL_LINKS_FOOT =
  " Moss Matrix: build your blend, browse ingredients, or read how to use sea moss gel at home. Not medical advice.";

function splitCautionsToBullets(text) {
  if (!text || !text.trim()) return ["Speak with a healthcare professional if pregnant, breastfeeding, on medication, managing a condition, or unsure."];
  const parts = text
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length ? parts : [text.trim()];
}

function pairingsFromBestWith(ing) {
  const raw = (ing.bestWith || "")
    .split(/,|·|\+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 6);
  if (!raw.length) {
    return [{ label: `${ing.name} + gold sea moss`, reason: "A simple starting point in the builder if you like mellow marine base notes." }];
  }
  return raw.map((bit) => ({
    label: `${ing.name} + ${bit}`,
    reason: `Often chosen together when people want ${ing.flavour?.toLowerCase() || "this profile"} beside other pantry notes in gel.`,
  }));
}

function defaultDetoxParagraphs(name) {
  return [
    `People sometimes talk about ${name} in the same breath as cleansing or detox-style routines online. Your body already has its own clearance systems. Moss Matrix presents ${name} as a traditional food-style botanical for flavour and routine variety, not as a detox treatment or parasite cleanse.`,
    "We do not present parasite-cleansing stories as evidence-based outcomes. If you have symptoms or concerns, speak with a qualified healthcare professional.",
    "This page is not medical advice.",
  ];
}

/**
 * @param {object} ing
 * @returns {IngredientLibraryV2}
 */
export function buildFallbackLibraryV2(ing) {
  const name = ing.name;
  const bullets = [
    ing.flavour ? `${ing.flavour} on the spoon` : `Distinct ${ing.category.toLowerCase()} character`,
    ing.traditionalUse ? ing.traditionalUse : `Popular in herbal kitchens as part of everyday food-style routines`,
    ing.bestWith ? `Often paired in gel with: ${ing.bestWith}` : "Works in small amounts while you find your own balance in the builder",
    ing.availableInBuilder
      ? "Available in Moss Matrix custom blends (curated launch menu on 500ml and 1 litre)"
      : "Educational guide in the ingredient library (not on the launch blend menu)",
    "Not medical advice",
  ].filter(Boolean).slice(0, 5);

  const nutrients = [
    `${name} is usually chosen as much for traditional use and taste as for any single nutrient headline in a small powdered spoonful.`,
    "If you need tailored nutrition advice, speak with a registered professional. Moss Matrix gels are food products with optional botanical powders.",
  ];

  const showDetox = DETOX_SECTION_IDS.has(ing.id);

  const faqs = [
    {
      q: `Can I add ${name} to sea moss gel?`,
      a: `When ${name} is on our launch blend menu, it ships as a powdered botanical in custom jars or pouches (500ml and 1 litre only). The full ingredient library is wider for reading; the builder stays curated so batches stay consistent. It is for kitchen-style routines, not to treat or prevent illness.${INTERNAL_LINKS_FOOT}`,
    },
    {
      q: `What does ${name} taste like?`,
      a: `We describe the spoon profile as: ${ing.flavour}. Strength depends on how much you add and what base you choose.`,
    },
    {
      q: `What pairs well with ${name}?`,
      a: `A simple starting point is what we list on this page under pairings and on the card: ${ing.bestWith || "gold or purple sea moss base"}.`,
    },
    {
      q: `Who should be careful with ${name}?`,
      a: `Read the cautions list on this page. If you are pregnant, breastfeeding, taking medication, buying for a child, or unsure, speak with a healthcare professional before using concentrated herbs or sea botanicals.`,
    },
  ];

  if (showDetox) {
    faqs.splice(2, 0, {
      q: `Is ${name} a detox ingredient?`,
      a: `We do not sell ${name} as a detox or cleanse treatment. Some people still choose it in food-style routines they describe that way. This page explains taste and tradition only.`,
    });
  }

  return {
    glance: { title: `${name} at a glance`, bullets },
    traditionalParagraphs: [ing.traditionalUse || `${name} has a long history in kitchens and herbal teas as a pantry or garden ingredient.`],
    whyPeopleChooseIt: {
      routine: ing.shortDescription || `Often chosen for simple Moss Matrix routines that favour ${ing.category.toLowerCase()} notes on the spoon.`,
      taste: ing.flavour || "Mild pantry character until you adjust the amount in the builder.",
      pairing: ing.bestWith ? `Works neatly beside ${ing.bestWith}.` : "Pairs with gold or purple sea moss and other powders you already like.",
    },
    nutrientsOrCompounds: nutrients,
    detoxFraming: showDetox
      ? { show: true, title: `Does ${name} support detox?`, paragraphs: defaultDetoxParagraphs(name) }
      : { show: false, paragraphs: [] },
    tasteInGel: [
      ing.whyAddToSeaMoss || `${name} adds its own character to sea moss gel so you can taste your choices before you change the recipe at home.`,
      `Expect ${ing.flavour || "a gentle botanical lift"} most clearly when the gel is plain or lightly sweetened.`,
    ],
    pairings: pairingsFromBestWith(ing),
    communityNotes: {
      intro:
        "Used this ingredient in your blend? Tell us how it fitted into your routine. We review stories before publishing and never present personal experiences as medical advice. We will add real customer notes here as people share how they use this ingredient in their routine.",
      placeholders: ["How do you use it?", "What did you pair it with?", "Would you choose it again?"],
    },
    cautionBullets: splitCautionsToBullets(ing.cautions),
    faqs,
    seoSummary: ing.shortDescription || ing.seoDescription || "",
    ctaLinks: [
      { label: `Build with ${name}`, href: "/#builder" },
      { label: "Shop sea moss", href: "/shop" },
      { label: "All ingredients", href: "/ingredients" },
      { label: "Blend routines", href: "/blends" },
    ],
  };
}

/**
 * Convert legacy library shape (v1) to v2.
 * @param {any} legacy
 * @param {object} ing
 * @returns {IngredientLibraryV2}
 */
export function libraryV2FromLegacy(legacy, ing) {
  const base = buildFallbackLibraryV2(ing);
  if (!legacy) return base;

  const often = legacy.oftenChosenFor || [];
  const routine = often[0]?.body || base.whyPeopleChooseIt.routine;
  const tasteCard = often.find((c) => /taste|bitter|warm|spice|sweet|tart|green|earthy/i.test(c.title))?.body || base.whyPeopleChooseIt.taste;
  const pairCard = often.find((c) => /pair|blend|kitchen/i.test(c.title))?.body || base.whyPeopleChooseIt.pairing;

  const pairingsV2 = (legacy.pairings || []).map((p) => ({
    label: p.title || p.partner,
    reason: [p.why, p.taste].filter(Boolean).join(" "),
    caution: p.caution,
  }));

  return {
    ...base,
    glance: legacy.glance || base.glance,
    traditionalParagraphs: legacy.traditionalHerbalBackground || base.traditionalParagraphs,
    whyPeopleChooseIt: {
      routine,
      taste: tasteCard,
      pairing: pairCard,
    },
    nutrientsOrCompounds: legacy.nutrientsAndCompounds || base.nutrientsOrCompounds,
    detoxFraming: legacy.detoxCleanseFraming
      ? { show: true, title: legacy.detoxCleanseFraming.title, paragraphs: legacy.detoxCleanseFraming.paragraphs }
      : base.detoxFraming,
    tasteInGel: legacy.tasteInSeaMossGel || base.tasteInGel,
    pairings: pairingsV2.length ? pairingsV2 : base.pairings,
    communityNotes: {
      intro: base.communityNotes.intro,
      placeholders: base.communityNotes.placeholders,
    },
    cautionBullets: legacy.cautionBullets || base.cautionBullets,
    faqs: base.faqs,
    ctaLinks: legacy.ctaLinks || base.ctaLinks,
  };
}

function deepMerge(a, b) {
  if (!b) return a;
  const out = { ...a };
  for (const k of Object.keys(b)) {
    const bv = b[k];
    if (bv === undefined) continue;
    if (Array.isArray(bv)) {
      out[k] = bv.slice();
    } else if (bv && typeof bv === "object") {
      const av = a[k];
      out[k] = deepMerge(av && typeof av === "object" && !Array.isArray(av) ? av : {}, bv);
    } else {
      out[k] = bv;
    }
  }
  return out;
}

/**
 * @param {object} ing
 */
export function resolveLibraryV2(ing) {
  let resolved;
  if (ing.library?.whyPeopleChooseIt) {
    resolved = deepMerge(buildFallbackLibraryV2(ing), ing.library);
  } else if (ing.library?.traditionalHerbalBackground || ing.library?.oftenChosenFor) {
    resolved = libraryV2FromLegacy(ing.library, ing);
  } else {
    resolved = buildFallbackLibraryV2(ing);
  }
  return deepMerge(resolved, RICH_LIBRARY_BY_ID[ing.id] || {});
}

/**
 * @param {object[]} raw
 */
export function attachIngredientLibraries(raw) {
  return raw.map((ing) => {
    const library = resolveLibraryV2(ing);
    const { library: _drop, ...rest } = ing;
    const seoPatch = RICH_SEO_BY_ID[ing.id];
    const seoTitle = brandSpace(
      seoPatch?.seoTitle || rest.seoTitle || `${ing.name} in sea moss gel | Moss Matrix`,
    );
    const seoDescription = brandSpace(
      seoPatch?.seoDescription ||
        rest.seoDescription ||
        library.seoSummary ||
        `Learn how ${ing.name} tastes in Moss Matrix sea moss gel, why people choose it in food-style routines, pairings, and cautions. Not medical advice.`,
    );
    return { ...rest, library, seoTitle, seoDescription };
  });
}

function brandSpace(s) {
  return (s || "").replace(/MossMatrix/g, "Moss Matrix");
}
